import DOMPurify from 'dompurify';
import { forwardRef } from 'react';

interface SafeHtmlProps {
  /** Raw HTML string to be sanitized and rendered */
  html: string | null | undefined;
  className?: string;
  /**
   * Use 'strict' for user-generated content (notifications, messages).
   * Use 'rich' for admin-controlled content (descriptions, hosting details).
   * Use 'permissive' only for trusted third-party HTML (e.g. PayPal SDK).
   */
  mode?: 'strict' | 'rich' | 'permissive';
}

const STRICT_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'span', 'a'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  FORBID_ATTR: ['style', 'onerror', 'onload'],
};

const RICH_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'li', 'ol',
    'span', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'blockquote', 'code', 'pre', 'hr', 'img', 'div',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'src', 'alt', 'width', 'height'],
};

/** Broad config for trusted 3rd-party HTML (e.g. PayPal buttons) */
const PERMISSIVE_CONFIG: DOMPurify.Config = {
  FORCE_BODY: true,
  ADD_TAGS: ['style', 'input', 'select', 'option', 'form', 'button', 'table', 'td', 'tr', 'img'],
  ADD_ATTR: ['target', 'type', 'name', 'value', 'action', 'method', 'enctype', 'src', 'alt', 'selected'],
};

export const SafeHtml = forwardRef<HTMLDivElement, SafeHtmlProps>(
  function SafeHtml({ html, className, mode = 'rich' }, ref) {
    const config =
      mode === 'strict' ? STRICT_CONFIG :
      mode === 'permissive' ? PERMISSIVE_CONFIG :
      RICH_CONFIG;

    const clean = DOMPurify.sanitize(html || '', config);

    return (
      <div
        ref={ref}
        className={className}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }
);
